'use client';

import { useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

// Just for fun, and the page says so. The score is a stable hash of the two names
// (order- and case-insensitive), so the same pair always gets the same number
// instead of a random one each click.
function loveScore(a: string, b: string): number {
  const key = [a, b]
    .map((s) => s.trim().toLowerCase().replace(/\s+/g, ' '))
    .sort()
    .join('+');
  let h = 2166136261;
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return 20 + ((h >>> 0) % 81);
}

function verdict(score: number): string {
  if (score >= 90) return 'A match made in heaven.';
  if (score >= 75) return 'Strong chemistry.';
  if (score >= 60) return 'Plenty to build on.';
  if (score >= 40) return 'Friends first, and who knows.';
  return 'Opposites attract, sometimes.';
}

export default function BrowserLoveCalculator() {
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [score, setScore] = useState<number | null>(null);

  function calculate() {
    if (!a.trim() || !b.trim()) return;
    setScore(loveScore(a, b));
    void consumeToolUsage('love-calculator', '').catch(() => undefined);
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Just for fun</span>
        <h2 className="tool-live-title">Love calculator</h2>
        <p className="tool-live-subtitle">
          Enter two names to get a love compatibility score. It is a playful name game, not science,
          and the names never leave your browser.
        </p>
      </div>
      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Your name</span>
            <input onChange={(e) => setA(e.target.value)} type="text" value={a} />
          </label>
          <label className="tool-live-field tool-live-field-full">
            <span>Their name</span>
            <input onChange={(e) => setB(e.target.value)} type="text" value={b} />
          </label>
          <div className="tool-live-actions">
            <button
              className="button"
              disabled={!a.trim() || !b.trim()}
              onClick={calculate}
              type="button"
            >
              Calculate love
            </button>
          </div>
        </section>
        <section className="tool-live-preview">
          {score !== null ? (
            <div className="tool-live-stats">
              <p className="tool-live-title">{score}%</p>
              <p className="tool-live-subtitle">
                {a.trim()} + {b.trim()}: {verdict(score)}
              </p>
            </div>
          ) : (
            <p className="tool-live-panel-note">Enter both names and press Calculate love.</p>
          )}
        </section>
      </div>
    </div>
  );
}
